import { useMutation } from "@tanstack/react-query";
import { Ticket, Plus, Trash2, Send, RefreshCw, Crown } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig, api } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import { Switch } from "@/components/ui/switch";
import { QuestionListEditor } from "@/components/panel/QuestionListEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface Props {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["tickets"]) => void;
  premium: boolean;
}

export function TicketsTab({ guildId, config, channels, roles, onChange, premium }: Props) {
  const cfg = config.tickets;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  const repostMut = useMutation({
    mutationFn: () => api.repostPanel(guildId, { section: "tickets" }),
    onSuccess: () =>
      toast.success("Existing panel message will refresh in Discord (no duplicate post)."),
    onError: (e: Error) => toast.error(e.message),
  });

  const createCategory = async () => {
    const name = prompt("New category name?");
    if (!name) return;
    try {
      await api.createCategory(guildId, name);
      toast.success("Category created. Refresh the page.");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Section
      icon={Ticket}
      title="Tickets"
      description="A button panel in a text channel. Members pick a category, the bot creates a private ticket channel — configure panel, transcript log, Admin/Dev roles, and categories here."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
      actions={
        <Button
          size="sm"
          variant="outline"
          className="w-full sm:w-auto shrink-0"
          onClick={() => repostMut.mutate()}
          disabled={repostMut.isPending}
        >
          <RefreshCw className={`h-4 w-4 ${repostMut.isPending ? "animate-spin" : ""}`} />
          Repost panel
        </Button>
      }
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-3 sm:p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">Quick flow</p>
        <ol className="list-decimal pl-4 space-y-1 break-words leading-snug">
          <li>Choose where the <strong>panel</strong> is posted and where <strong>transcripts</strong> go.</li>
          <li>Set <strong>Admin</strong> and <strong>Dev</strong> roles (who gets channel access and default pings).</li>
          <li>Add <strong>categories</strong> — each is one button and its own parent Discord category for new channels.</li>
          <li>
            Save, then <strong>Repost panel</strong> to refresh the same Discord message (no duplicate posts).
          </li>
        </ol>
      </div>

      <Card className="p-4 bg-muted/30">
        <h4 className="font-semibold mb-2 text-sm">Slash commands (inside ticket channels)</h4>
        <div className="grid sm:grid-cols-2 gap-1.5 text-xs text-muted-foreground">
          <span>
            <code className="text-primary font-mono">/adduser</code> · add member to ticket
          </span>
          <span>
            <code className="text-primary font-mono">/removeuser</code> · remove member
          </span>
          <span>
            <code className="text-primary font-mono">/ticket lock</code> · lock (no auto-close)
          </span>
          <span>
            <code className="text-primary font-mono">/ticket unlock</code> · unlock again
          </span>
          <span className="sm:col-span-2">
            <code className="text-primary font-mono">/ticket rename</code> · rename channel (ticket admin)
          </span>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Panel channel"
          hint="The channel that will contain the public embed with Open ticket buttons. Users see this, not the ticket thread itself."
        >
          <ChannelPicker
            channels={channels}
            value={cfg.panel_channel_id}
            onChange={(v) => update("panel_channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
        <Field
          label="Transcript channel"
          hint="Log channel for a copy when a ticket is closed (and feedback flow, if you use the Ticket feedback module staff-side)."
        >
          <ChannelPicker
            channels={channels}
            value={cfg.transcript_channel_id}
            onChange={(v) =>
              update("transcript_channel_id", v === "__none__" ? "" : v)
            }
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Panel style"
          hint="Sections = Discord Components v2 (TextDisplay + Section + button accessory). No embed — panel title/description become text blocks. Reactions = emoji under the message. Max ~15 categories in Sections mode."
        >
          <Select
            value={cfg.panel_mode || "select"}
            onValueChange={(v) =>
              update(
                "panel_mode",
                v === "buttons"
                  ? "buttons"
                  : v === "reactions"
                    ? "reactions"
                    : v === "sections"
                      ? "sections"
                      : "select",
              )
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="select">Dropdown / select menu</SelectItem>
              <SelectItem value="buttons">Buttons (components v1)</SelectItem>
              <SelectItem value="sections">Sections (components v2)</SelectItem>
              <SelectItem value="reactions">Reactions (emoji)</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border p-3 min-w-0">
          <div className="min-w-0 pr-0 sm:pr-2">
            <div className="text-sm font-medium">DM user when ticket opens</div>
            <div className="text-xs text-muted-foreground break-words leading-snug">
              Sends the user a private message with the new ticket link.
            </div>
          </div>
          <Switch
            className="shrink-0 self-end sm:self-auto"
            checked={Boolean(cfg.dm_user_on_open)}
            onCheckedChange={(v) => update("dm_user_on_open", v)}
          />
        </div>
      </div>

      <Field
        label="Panel title"
        hint="Optional. Leave empty if you only want description and/or image."
      >
        <Input
          value={cfg.panel_title}
          placeholder="Your server’s title"
          onChange={(e) => update("panel_title", e.target.value)}
        />
      </Field>

      <Field
        label="Panel description"
        hint="Optional. Use line breaks here — they appear in Discord as separate lines."
      >
        <Textarea
          value={cfg.panel_description}
          rows={3}
          placeholder="Write your own intro, rules, response times…"
          onChange={(e) => update("panel_description", e.target.value)}
          className="min-h-[5.5rem] max-h-48 sm:max-h-64 overflow-y-auto text-[15px] sm:text-sm resize-y"
        />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Panel image URL (optional)"
          hint="Paste a direct image link (https). Where it appears is set next."
        >
          <Input
            value={cfg.panel_image_url ?? ""}
            placeholder="https://cdn.example.com/banner.png"
            onChange={(e) => update("panel_image_url", e.target.value)}
          />
        </Field>
        <Field
          label="Image placement"
          hint="Thumbnail = small corner. Large image = wide banner under the text."
        >
          <Select
            value={cfg.panel_image_position ?? "none"}
            onValueChange={(v) =>
              update(
                "panel_image_position",
                v === "thumbnail" || v === "image" ? v : "none",
              )
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No image</SelectItem>
              <SelectItem value="thumbnail">Thumbnail (corner)</SelectItem>
              <SelectItem value="image">Large image (below)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Footer text (optional)"
          hint="Small line at the bottom of the embed. Leave empty to hide."
        >
          <Input
            value={cfg.panel_footer_text ?? ""}
            onChange={(e) => update("panel_footer_text", e.target.value)}
          />
        </Field>
        <Field
          label="Categories block title (optional)"
          hint="Heading above the category list. Leave empty so only the list shows (no extra title)."
        >
          <Input
            value={cfg.panel_categories_heading ?? ""}
            onChange={(e) => update("panel_categories_heading", e.target.value)}
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Admin role"
          hint="Full ticket management (slash commands, close/rename, etc.). Gets channel access and is pinged when no per-category ping roles are set."
        >
          <MultiSelect
            options={roles.map((r) => ({ id: r.id, name: r.name, color: r.color }))}
            value={cfg.admin_role_ids}
            onChange={(v) => update("admin_role_ids", v)}
            prefix="@"
          />
        </Field>
        <Field
          label="Dev role"
          hint="Technical / bug tickets — gets channel access and is included in default pings with Admin when a category has no ping roles."
        >
          <MultiSelect
            options={roles.map((r) => ({ id: r.id, name: r.name, color: r.color }))}
            value={cfg.dev_role_ids}
            onChange={(v) => update("dev_role_ids", v)}
            prefix="@"
          />
        </Field>
      </div>

      {/* Auto-close on inactivity */}
      <div className="pt-4 border-t border-border space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border p-3 min-w-0">
          <div className="min-w-0 pr-0 sm:pr-2">
            <div className="text-sm font-medium">Auto-close inactive tickets</div>
            <div className="text-xs text-muted-foreground break-words leading-snug">
              Optional. When a ticket has had no messages for the set duration, the bot posts a
              notice with Close Ticket / Cancel Inactivity buttons. If nobody replies or clicks
              Cancel, it closes automatically after the second duration below (transcript saved as
              usual). Any reply cancels it automatically.
            </div>
          </div>
          <Switch
            className="shrink-0 self-end sm:self-auto"
            checked={Boolean(cfg.auto_close?.enabled)}
            onCheckedChange={(v) =>
              update("auto_close", {
                ...(cfg.auto_close || { inactivity_hours: 24, close_after_hours: 24 }),
                enabled: v,
              })
            }
          />
        </div>

        {cfg.auto_close?.enabled && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300 grid sm:grid-cols-2 gap-4 max-w-lg">
            <Field
              label="Warn after (hours)"
              hint="Hours of silence before the notice is posted. Decimals work (e.g. 0.05 ≈ 3 min) for quick testing."
            >
              <Input
                type="number"
                min={0.02}
                max={720}
                step={0.01}
                value={cfg.auto_close?.inactivity_hours ?? 24}
                onChange={(e) =>
                  update("auto_close", {
                    ...(cfg.auto_close || { enabled: true, close_after_hours: 24 }),
                    inactivity_hours: Math.max(0.02, Number(e.target.value) || 24),
                  })
                }
              />
            </Field>
            <Field
              label="Auto-close after warning (hours)"
              hint="Hours after the notice before the ticket closes on its own, if nobody replies or cancels."
            >
              <Input
                type="number"
                min={0.02}
                max={720}
                step={0.01}
                value={cfg.auto_close?.close_after_hours ?? 24}
                onChange={(e) =>
                  update("auto_close", {
                    ...(cfg.auto_close || { enabled: true, inactivity_hours: 24 }),
                    close_after_hours: Math.max(0.02, Number(e.target.value) || 24),
                  })
                }
              />
            </Field>
          </div>
        )}
      </div>

      {/* AI Assistant Section */}
      <div className="pt-4 border-t border-border space-y-4 relative">
        <div className={!premium ? "pointer-events-none opacity-40 blur-[1px]" : ""}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-primary/20 bg-primary/5 p-4">
            <div className="min-w-0 pr-0 sm:pr-2">
              <div className="text-lg font-bold flex items-center gap-2">
                 <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                    <Send className="h-3 w-3 text-primary-foreground" />
                 </div>
                 Areon AI Ticket Pilot
                 <Badge variant="premium" className="ml-2">PREMIUM</Badge>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                AI automatically tries to solve tickets using your Knowledge Base before staff is pinged.
              </div>
            </div>
            <Switch
              checked={Boolean(cfg.ai_assistant?.enabled)}
              onCheckedChange={(v) => update("ai_assistant", { ...(cfg.ai_assistant || {}), enabled: v } as any)}
            />
          </div>

          {cfg.ai_assistant?.enabled && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-4 mt-4">
              <div className="flex justify-between items-end gap-4">
                <div className="flex-1">
                  <Field 
                    label="Knowledge Base / Custom Prompt" 
                    hint="Provide all rules, FAQ, and server info. AI uses this to answer. Be as detailed as possible."
                  >
                    <Textarea
                      value={cfg.ai_assistant.knowledge_base}
                      rows={6}
                      placeholder="Example: Rules: 1. No RDM. 2. No VDM. For whitelist, go to #applications... etc."
                      onChange={(e) => update("ai_assistant", { ...cfg.ai_assistant, knowledge_base: e.target.value } as any)}
                      className="bg-background font-sans leading-relaxed"
                    />
                  </Field>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  className="shrink-0 mb-6"
                  onClick={() => {
                    const template = 
                      "**[Server Information]**\n" +
                      "Name: Areon Roleplay City\n" +
                      "IP Address: play.areonrp.top (Port: 30120)\n\n" +
                      "**[Frequently Asked Questions]**\n" +
                      "1. Whitelist Application: To apply for whitelist, go to the #applications channel on Discord and submit your form. Staff reviews them within 12 hours.\n" +
                      "2. Server Rules: RDM (Random Deathmatch) and VDM (Vehicle Deathmatch) are strictly prohibited. In character rules must be followed at all times.\n" +
                      "3. Connect Issues: If you cannot connect, clear your FiveM cache and restart your client. If the issue persists, submit your logs in this ticket.\n\n" +
                      "**[Escalation Guidelines]**\n" +
                      "For billing issues, active rule breakers, or staff applications, tell the user to wait for a moderator and do not try to resolve it autonomously.";
                    update("ai_assistant", { ...cfg.ai_assistant, knowledge_base: template } as any);
                    toast.success("Loaded professional AI ticketing prompt template!");
                  }}
                >
                  Load Template
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Modal Title" hint="What users see when asked to describe their problem.">
                  <Input
                    value={cfg.ai_assistant.modal_title}
                    onChange={(e) => update("ai_assistant", { ...cfg.ai_assistant, modal_title: e.target.value } as any)}
                  />
                </Field>
                <Field label="Input Label" hint="The text above the input field in the modal.">
                  <Input
                    value={cfg.ai_assistant.modal_label}
                    onChange={(e) => update("ai_assistant", { ...cfg.ai_assistant, modal_label: e.target.value } as any)}
                  />
                </Field>
              </div>

              {/* Live Discord Ticket Message Preview Card */}
              <div className="rounded-xl border border-border bg-black/40 p-4 mt-6">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Discord Ticket Chat Live Preview
                </div>
                <div className="flex gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center font-bold text-xs shrink-0 border border-primary/20">
                     A
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-sm text-foreground">Areon</span>
                      <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded leading-none">BOT</span>
                      <span className="text-xs text-muted-foreground">Today at 5:53 PM</span>
                    </div>
                    <div className="mt-2 p-3.5 rounded-lg border border-primary/10 bg-primary/5 text-sm leading-relaxed max-w-2xl">
                      <div className="font-bold text-sm flex items-center gap-1.5 mb-2 text-foreground">
                         🤖 Areon AI Ticket Pilot
                      </div>
                      <p className="text-muted-foreground text-xs mb-3">
                        Hello <strong className="text-primary font-medium">@Member</strong>! I have analyzed your support query and found the matching information in our database:
                      </p>
                      <div className="bg-black/35 rounded border border-border/40 p-3 mb-3 text-xs space-y-1.5 font-sans">
                         <div className="text-foreground font-semibold">ℹ️ Knowledge Base Highlights:</div>
                         {cfg.ai_assistant.knowledge_base ? (
                           <div className="whitespace-pre-line text-muted-foreground leading-normal font-sans text-xs">
                             {cfg.ai_assistant.knowledge_base.slice(0, 320)}
                             {cfg.ai_assistant.knowledge_base.length > 320 && "..."}
                           </div>
                         ) : (
                           <div className="text-muted-foreground italic">
                             Write your FAQ, server rules, or whitelist links in the Knowledge Base field above to preview the AI response formatting here.
                           </div>
                         )}
                      </div>
                      <p className="text-muted-foreground text-xs leading-normal">
                         Did this solve your issue? If so, you can click <strong className="text-foreground">Mark Resolved</strong> below. If you still need assistance, click <strong className="text-foreground">Ping Staff</strong> to alert the moderators.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1 pointer-events-none">
                         ✅ Mark Resolved
                      </button>
                      <button className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1 pointer-events-none">
                         🔔 Ping Staff
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Compact Premium Lock Overlay */}
        {!premium && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/40 backdrop-blur-[2px] rounded-xl border border-dashed border-amber-500/20 p-6 text-center">
            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 shadow-md shadow-amber-500/20 mb-3 animate-pulse">
              <Crown className="h-5 w-5 text-zinc-900" />
            </div>
            <h4 className="font-bold text-base mb-1">AI Ticket Pilot is Premium</h4>
            <p className="text-xs text-muted-foreground max-w-sm mb-3">
              Automate ticket resolution with artificial intelligence using your custom knowledge base.
            </p>
            <Button
              size="sm"
              variant="premium"
              onClick={() => {
                // Navigate to billing
                window.location.href = `/premium/${guildId}`;
              }}
            >
              <Crown className="h-3.5 w-3.5 mr-1.5" />
              Upgrade to Premium
            </Button>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 space-y-1">
            <h3 className="font-semibold text-base sm:text-lg">Ticket categories</h3>
            <p className="text-xs text-muted-foreground break-words leading-snug">
              Button combinations on the panel. Each opens a ticket in its own
              Discord category.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 w-full sm:w-auto sm:shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto justify-center"
              onClick={createCategory}
            >
              <Plus className="h-4 w-4 shrink-0" />
              <span className="truncate">New Discord category</span>
            </Button>
            <Button
              size="sm"
              className="w-full sm:w-auto justify-center"
              onClick={() =>
                update("categories", [
                  ...cfg.categories,
                  {
                    key: `cat_${Date.now()}`,
                    label: "",
                    emoji: "🎫",
                    description: "",
                    ticket_message: "",
                    category_id: "",
                    ping_role_ids: [],
                  },
                ])
              }
            >
              <Plus className="h-4 w-4 shrink-0" />
              Add button
            </Button>
          </div>
        </div>

        {cfg.categories.length === 0 && (
          <div className="text-center py-8 border border-dashed border-border rounded-lg text-sm text-muted-foreground">
            No categories yet. Add at least one.
          </div>
        )}

        <div className="space-y-3">
          {cfg.categories.map((cat, idx) => (
            <Card key={cat.key || idx} className="p-3 sm:p-4 space-y-3 bg-muted/30 min-w-0 overflow-hidden">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Input
                    className="w-12 sm:w-16 shrink-0 text-center text-base sm:text-sm px-1"
                    value={cat.emoji}
                    onChange={(e) => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], emoji: e.target.value };
                      update("categories", c);
                    }}
                  />
                  <Input
                    className="min-w-0 flex-1 text-base sm:text-sm"
                    value={cat.label}
                    placeholder="Button label"
                    onChange={(e) => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], label: e.target.value };
                      update("categories", c);
                    }}
                  />
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0 self-end sm:self-auto"
                  onClick={() =>
                    update(
                      "categories",
                      cfg.categories.filter((_, i) => i !== idx),
                    )
                  }
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
              <Field
                label="Panel description"
                hint="Shown next to the button/dropdown option when picking a category — not shown inside the ticket."
              >
                <Input
                  value={cat.description}
                  placeholder="Button description"
                  className="text-base sm:text-sm"
                  onChange={(e) => {
                    const c = [...cfg.categories];
                    c[idx] = { ...c[idx], description: e.target.value };
                    update("categories", c);
                  }}
                />
              </Field>
              <Field
                label="Ticket welcome message"
                hint="Shown inside the opened ticket channel instead of the panel description above. Leave empty to show nothing here."
              >
                <Textarea
                  value={cat.ticket_message || ""}
                  placeholder="e.g. Please describe your issue in detail and a staff member will assist you shortly."
                  rows={2}
                  className="text-base sm:text-sm"
                  onChange={(e) => {
                    const c = [...cfg.categories];
                    c[idx] = { ...c[idx], ticket_message: e.target.value };
                    update("categories", c);
                  }}
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Discord category">
                  <ChannelPicker
                    channels={channels}
                    value={cat.category_id}
                    typeFilter={4}
                    onChange={(v) => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], category_id: v === "__none__" ? "" : v };
                      update("categories", c);
                    }}
                  />
                </Field>
                <Field label="Ping roles (on open)">
                  <MultiSelect
                    options={roles.map((r) => ({
                      id: r.id,
                      name: r.name,
                      color: r.color,
                    }))}
                    value={cat.ping_role_ids}
                    onChange={(v) => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], ping_role_ids: v };
                      update("categories", c);
                    }}
                    prefix="@"
                  />
                </Field>
              </div>

              <div className="pt-1 border-t border-border/40 space-y-3">
                <div className="pt-2">
                  <div className="text-sm font-medium">Intake form (optional)</div>
                  <p className="text-xs text-muted-foreground">
                    Collect answers before the ticket opens — they become the first message in the ticket.
                  </p>
                </div>

                <div className="flex gap-2 p-1 bg-muted/40 rounded-lg border border-border max-w-md">
                  <button
                    type="button"
                    className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
                      (cat.question_mode ?? "direct") === "direct"
                        ? "bg-background text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], question_mode: "direct" };
                      update("categories", c);
                    }}
                  >
                    Direct form
                  </button>
                  <button
                    type="button"
                    className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
                      cat.question_mode === "choice"
                        ? "bg-background text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], question_mode: "choice" };
                      update("categories", c);
                    }}
                  >
                    Dropdown choice first
                  </button>
                </div>

                {(cat.question_mode ?? "direct") === "direct" ? (
                  <QuestionListEditor
                    questions={cat.questions || []}
                    maxQuestions={5}
                    onChange={(next) => {
                      const c = [...cfg.categories];
                      c[idx] = { ...c[idx], questions: next };
                      update("categories", c);
                    }}
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      User first picks one option from a dropdown, then fills in that option's own form — e.g. "Paysafe → PayPal" asks for a code and an email, while "Crypto → Bank" could ask for something else entirely.
                    </p>
                    {(cat.option_groups || []).map((opt, optIdx) => (
                      <div key={opt.id || optIdx} className="rounded-lg border border-border/70 p-3 space-y-3 bg-muted/10">
                        <div className="flex gap-2 items-center">
                          <Input
                            className="text-base sm:text-sm font-medium"
                            value={opt.label}
                            placeholder="Dropdown option label, e.g. Paysafe → PayPal"
                            onChange={(e) => {
                              const c = [...cfg.categories];
                              const groups = [...(cat.option_groups || [])];
                              groups[optIdx] = { ...opt, label: e.target.value };
                              c[idx] = { ...c[idx], option_groups: groups };
                              update("categories", c);
                            }}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="shrink-0"
                            onClick={() => {
                              const c = [...cfg.categories];
                              c[idx] = { ...c[idx], option_groups: (cat.option_groups || []).filter((_, i) => i !== optIdx) };
                              update("categories", c);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                        <Input
                          className="text-base sm:text-sm"
                          value={opt.description ?? ""}
                          placeholder="Dropdown option description (optional)"
                          onChange={(e) => {
                            const c = [...cfg.categories];
                            const groups = [...(cat.option_groups || [])];
                            groups[optIdx] = { ...opt, description: e.target.value };
                            c[idx] = { ...c[idx], option_groups: groups };
                            update("categories", c);
                          }}
                        />
                        <QuestionListEditor
                          questions={opt.questions || []}
                          maxQuestions={5}
                          onChange={(next) => {
                            const c = [...cfg.categories];
                            const groups = [...(cat.option_groups || [])];
                            groups[optIdx] = { ...opt, questions: next };
                            c[idx] = { ...c[idx], option_groups: groups };
                            update("categories", c);
                          }}
                        />
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-dashed"
                      onClick={() => {
                        const c = [...cfg.categories];
                        c[idx] = {
                          ...c[idx],
                          option_groups: [
                            ...(cat.option_groups || []),
                            { id: `opt_${Date.now()}`, label: "New option", description: "", questions: [] },
                          ],
                        };
                        update("categories", c);
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      Add dropdown option
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Section>
  );
}
